import React, { useEffect, useMemo, useState } from 'react';
import TableUsers from '../../components/admin/Users/TableUsers';
import { Modal, notification, Spin, Alert, Button } from 'antd';
import UserForm from 'components/forms/UserForm';
import ChangeUserPasswordForm from 'components/forms/ChangeUserPasswordForm';
import { STATUS_ERROR, STATUS_LOADING, useCallGetApi } from 'api/useCallApi';
import ErrorComponent from 'components/ErrorComponent';
import { putToAPi } from 'api';
import { handleApiError, handleApiResponse } from 'utils/handlers';
import { postToApi } from 'api/axios';
import { ReloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

const defaultModalProps = {
  children: null,
  title: 'Error',
  open: false,
  width: 600,
  className: 'top-5',
};

function Users() {
  const [modalProps, setModalProps] = useState(defaultModalProps);
  const [users, callUsers, statusUsers, errorInfo] = useCallGetApi('/user/all');
  // El catálogo dice, por rol, si el actor puede gestionarlo (`canManage` del backend).
  // Se usa para no ofrecer "Cambiar contraseña" sobre un usuario que igual daría 403 —
  // el candado real vive en la API; esto solo evita mostrar una acción que no procede.
  const [rolesCatalogo, callRoles] = useCallGetApi('/user/roles');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tiposGestionables = useMemo(
    () => new Set((rolesCatalogo || []).filter((r) => r.gestionable).map((r) => r.value)),
    [rolesCatalogo]
  );

  const activeUsers = useMemo(() => {
    return users?.filter((user) => user.active === true);
  }, [users]);
  const inactiveUsers = useMemo(() => {
    return users?.filter((user) => user.active === false);
  }, [users]);

  useEffect(() => {
    callUsers();
  }, [callUsers]);

  useEffect(() => {
    callRoles();
  }, [callRoles]);

  const reloadUsers = () => callUsers();

  const addUser = async (value) => {
    setIsSubmitting(true);
    try {
      delete value.repeatPassword;
      const response = await postToApi('/user/add', {
        ...value,
        active: true,
      });

      notification.success({
        message: 'Usuario creado exitosamente',
        description: response.message || 'El usuario ha sido agregado al sistema',
      });

      reloadUsers();
      setModalProps(defaultModalProps);
      return { resetFields: true };
    } catch (error) {
      handleApiError(error);
      return { resetFields: false };
    } finally {
      setIsSubmitting(false);
    }
  };
  const openAddUser = async () => {
    setModalProps({
      open: true,
      children: (
        <UserForm
          buttonLabel="Agregar Usuario"
          submitForm={addUser}
          isSubmitting={isSubmitting}
          onCancel={() => setModalProps(defaultModalProps)}
        />
      ),
      title: 'Agregar Nuevo Usuario',
    });
  };

  const editUser = async (value, formProps) => {
    setIsSubmitting(true);
    try {
      const { initialValues } = formProps;
      // Migración rut→id: el usuario se identifica por su id.
      const putResponse = await putToAPi(`/user/edit/${initialValues.id}`, value);

      notification.success({
        message: 'Usuario actualizado exitosamente',
        description: putResponse.message || 'Los cambios han sido guardados',
      });

      reloadUsers();
      setModalProps(defaultModalProps);
      return { resetFields: true };
    } catch (error) {
      handleApiError(error);
      return { resetFields: false };
    } finally {
      setIsSubmitting(false);
    }
  };

  const changeUserPassword = async (user, values) => {
    setIsSubmitting(true);
    try {
      const putResponse = await putToAPi(`/user/${user.id}/password`, values);

      notification.success({
        message: 'Contraseña actualizada',
        description:
          putResponse.message ||
          `${user.fullname || user.rut} ya puede entrar con la contraseña nueva.`,
      });

      setModalProps(defaultModalProps);
      return { resetFields: true };
    } catch (error) {
      handleApiError(error);
      return { resetFields: false };
    } finally {
      setIsSubmitting(false);
    }
  };

  const openChangePassword = (user) => {
    setModalProps({
      open: true,
      children: (
        <ChangeUserPasswordForm
          usuario={user}
          submitForm={(values) => changeUserPassword(user, values)}
          isSubmitting={isSubmitting}
          onCancel={() => setModalProps(defaultModalProps)}
        />
      ),
      title: 'Cambiar contraseña',
    });
  };

  const toggleActiveUser = async (user) => {
    try {
      const putResponse = await putToAPi('/user/toggle-active', {
        id: user.id,
      });

      const action = user.active ? 'desactivado' : 'activado';
      notification.success({
        message: `Usuario ${action} exitosamente`,
        description: putResponse.message || `El usuario ha sido ${action}`,
      });

      reloadUsers();
      return { resetFields: true };
    } catch (error) {
      handleApiError(error);
      return { resetFields: false };
    }
  };

  const openEditUser = (user) => {
    setModalProps({
      open: true,
      children: (
        <UserForm
          buttonLabel="Guardar Cambios"
          formProps={{
            initialValues: user,
          }}
          editing={true}
          submitForm={editUser}
          isSubmitting={isSubmitting}
          onCancel={() => setModalProps(defaultModalProps)}
        />
      ),
      title: 'Editar Usuario',
    });
  };

  if (statusUsers === STATUS_ERROR) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <Alert
          message="Error al cargar usuarios"
          description={errorInfo?.message || 'No se pudieron cargar los usuarios del sistema'}
          type="error"
          showIcon
          action={
            <Button size="small" danger icon={<ReloadOutlined />} onClick={reloadUsers}>
              Reintentar
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="users">
      <TableUsers
        usersActive={activeUsers}
        usersInactive={inactiveUsers}
        reloadUsers={reloadUsers}
        openAddUser={openAddUser}
        openEditUser={openEditUser}
        openChangePassword={openChangePassword}
        tiposGestionables={tiposGestionables}
        toggleActiveUser={toggleActiveUser}
        loading={statusUsers === STATUS_LOADING}
        error={statusUsers === STATUS_ERROR ? errorInfo?.message : null}
      />

      <Modal
        {...modalProps}
        footer={false}
        onCancel={() => setModalProps(defaultModalProps)}
        destroyOnHidden
        maskClosable={false}
      />
    </div>
  );
}

export default Users;
